import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Loader2, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { NEWSLETTER_WEBHOOK, isNewsletterLive } from '../newsletter/config';

// ============================================================
// Copy local, no pasa por i18n/translations.ts. Así el bloque
// completo (incluidos los estados de envío) vive en un solo
// archivo y se edita sin tocar el diccionario global.
//
// NOTA DE HONESTIDAD: no prometemos cadencia. La versión anterior
// decía "un correo cada semana" y esa es una promesa que hay que
// sostener todas las semanas. Esta versión promete avisar cuando
// se publica algo, que es lo que realmente va a pasar.
// ============================================================
const UI = {
  es: {
    title: 'Recibe la próxima decisión analizada.',
    subtitle: 'Te escribimos cuando publicamos un análisis nuevo. Sin relleno, sin urgencia inventada y sin promesas de puntos.',
    placeholder: 'Ingresa tu correo electrónico',
    label: 'Correo electrónico',
    cta: 'Suscribirme',
    sending: 'Enviando',
    successTitle: 'Listo, recibimos tu correo.',
    successBody: 'Te vamos a escribir cuando publiquemos el próximo análisis. Puedes darte de baja en cualquier momento.',
    errorTitle: 'No pudimos registrar tu correo.',
    errorBody: 'Intenta de nuevo, o escríbenos a insights@blinkblis.com.',
    retry: 'Intentar de nuevo',
    disclaimerPre: 'Al suscribirte aceptas nuestra',
    privacy: 'Política de Privacidad',
    and: 'y los',
    terms: 'Términos de Servicio'
  },
  en: {
    title: 'Get the next decision we analyze.',
    subtitle: 'We email you when we publish a new analysis. No filler, no manufactured urgency, no promises about points.',
    placeholder: 'Enter your email address',
    label: 'Email address',
    cta: 'Subscribe',
    sending: 'Sending',
    successTitle: 'Got it, your email is in.',
    successBody: 'We will write to you when the next analysis goes up. You can unsubscribe at any time.',
    errorTitle: 'We could not register your email.',
    errorBody: 'Please try again, or write to us at insights@blinkblis.com.',
    retry: 'Try again',
    disclaimerPre: 'By subscribing you agree to our',
    privacy: 'Privacy Policy',
    and: 'and',
    terms: 'Terms of Service'
  }
};
type Status = 'idle' | 'sending' | 'done' | 'error';
export function Newsletter() {
  const {
    language
  } = useLanguage();
  const lang = language === 'en' ? 'en' : 'es';
  const t = UI[lang];
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    setStatus('sending');
    const w = window as any;
    if (typeof w.fbq === 'function') {
      w.fbq('trackCustom', 'NewsletterSignup', {
        path: window.location.pathname,
        lang
      });
    }
    if (!isNewsletterLive()) {
      setStatus('done');
      return;
    }
    try {
      await fetch(NEWSLETTER_WEBHOOK, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: value,
          source: 'blinkblis.com',
          path: window.location.pathname,
          lang,
          ts: new Date().toISOString()
        })
      });
      setStatus('done');
    } catch {
      setStatus('error');
    }
  };
  return <section className="py-20 md:py-32 relative z-10" data-id="element-263" data-mp="1r64az6j3byef">
      <div className="max-w-4xl mx-auto px-5 sm:px-6" data-id="element-264" data-mp="1r4je3a9b03de">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="glass-panel rounded-[2.5rem] p-8 md:p-16 border border-white/10 text-center relative overflow-hidden" data-id="element-265" data-mp="1m7j5osozeoj4">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,106,0,0.1),transparent_70%)]" data-id="element-266" data-mp="1ynqyr2mt9vls" />

          <div className="relative z-10" data-id="element-267" data-mp="23gk33pedb1xc">
            {status === 'done' ? <motion.div initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4
          }} role="status" aria-live="polite" className="max-w-md mx-auto" data-id="element-268" data-mp="1pp5jbgcmywp8">
                <div className="w-14 h-14 rounded-full bg-accent/15 border border-accent/40 flex items-center justify-center mx-auto mb-6" data-id="element-269" data-mp="2md3eymgk33xl">
                  <Check className="w-7 h-7 text-accent" data-id="element-270" data-mp="28gq07l3epgmt" />
                </div>
                <h2 className="text-[28px] leading-[1.15] md:text-4xl font-display font-medium mb-4" data-id="element-271" data-mp="2dblt0nggw80z">
                  {t.successTitle}
                </h2>
                <p className="text-white/55 text-[16px] md:text-[17px] font-light leading-relaxed" data-id="element-272" data-mp="11yjoiqrn78x6">
                  {t.successBody}
                </p>
              </motion.div> : <>
                <h2 className="text-[40px] leading-[1.05] md:text-5xl lg:text-6xl font-display font-medium mb-4 md:mb-6" data-id="element-273" data-mp="pemgbpoo1p3c">
                  {t.title}
                </h2>
                <p className="text-white/60 text-[18px] leading-[1.5] md:text-xl mb-10 md:mb-12 max-w-2xl mx-auto font-light" data-id="element-274" data-mp="yfub3xrz47so">
                  {t.subtitle}
                </p>

                <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" onSubmit={handleSubmit} data-id="element-275" data-mp="6p6biq5y7ah4">
                  <label htmlFor="newsletter-email" className="sr-only" data-id="element-276" data-mp="ki5q8pxwp0nm">
                    {t.label}
                  </label>
                  <input id="newsletter-email" name="email" type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} disabled={status === 'sending'} placeholder={t.placeholder} className="flex-1 bg-surface/50 border border-white/10 rounded-full px-6 md:px-8 h-[56px] md:h-auto py-4 text-[16px] text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-white/30 focus:bg-surface/80 transition-all font-light shadow-[inset_0_2px_8px_rgba(0,0,0,0.2)] disabled:opacity-60" required data-id="element-277" data-mp="fo1i43cwmmky" />
                  <button type="submit" disabled={status === 'sending'} className="bg-white text-background px-8 h-[56px] md:h-auto py-4 rounded-full text-[16px] md:text-base font-medium md:hover:bg-white/90 md:hover:-translate-y-0.5 md:hover:shadow-[0_8px_20px_rgba(255,255,255,0.15)] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-300 flex items-center justify-center gap-2 shrink-0 disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-278" data-mp="193xfhaqlwozr">
                    {status === 'sending' ? <>
                        {t.sending}
                        <Loader2 className="w-4 h-4 animate-spin" data-id="element-279" data-mp="92tp0vaohmo2" />
                      </> : <>
                        {t.cta}
                        <ArrowRight className="w-4 h-4" data-id="element-280" data-mp="3vbl5l9sedef0" />
                      </>}
                  </button>
                </form>

                {status === 'error' && <div role="alert" className="max-w-md mx-auto mt-5 flex items-start gap-3 text-left rounded-2xl border border-white/15 bg-white/[0.04] px-5 py-4" data-id="element-281" data-mp="3m9vde1qoybg6">
                    <AlertCircle className="w-[18px] h-[18px] text-accent shrink-0 mt-0.5" data-id="element-282" data-mp="1khz46yrzxdok" />
                    <div data-id="element-283" data-mp="1fkyxqmr8i8wx">
                      <p className="text-white/90 text-[15px] font-medium" data-id="element-284" data-mp="1fkgvn9hw50h3">
                        {t.errorTitle}
                      </p>
                      <p className="text-white/50 text-[14px] font-light mt-1" data-id="element-285" data-mp="tdemhku4h67r">
                        {t.errorBody}
                      </p>
                    </div>
                  </div>}

                <p className="text-white/30 text-[13px] md:text-xs mt-6 font-light" data-id="element-286" data-mp="jnbikplgq7un">
                  {t.disclaimerPre}{' '}
                  <Link to="/privacidad" className="underline hover:text-white/60" data-id="element-287" data-mp="5naivdgxp6gr">
                    {t.privacy}
                  </Link>{' '}
                  {t.and}{' '}
                  <Link to="/terminos" className="underline hover:text-white/60" data-id="element-288" data-mp="3rvtpo8xtu90q">
                    {t.terms}
                  </Link>
                  .
                </p>
              </>}
          </div>
        </motion.div>
      </div>
    </section>;
}